import { useContext } from "react";
import { ValidationError, validationFailed, ValidationResult } from "sprint-common-web";

import { CreateEntityResponse } from "../../../common/api/common/CreateEntityResponse";
import { AppConfigContext } from "../../../core/configuration/Configuration";
import { useAuthorizedFetch } from "../../../core/data-access/useAuthorizedFetch";
import { CreateAncillaryRequest } from "./CreateAncillaryRequest";
import { GetAncillariesBySimilarTitleResponse } from "./GetAncillariesBySimilarTitleResponse";
import { GetAncillaryListRequest } from "./GetAncillaryListRequest";
import { GetAncillaryListResponse } from "./GetAncillaryListResponse";
import { MergeAncillariesRequest } from "./MergeAncillariesRequest";
import { MergeAncillariesResponse } from "./MergeAncillariesResponse";
import { UpdateAncillaryRequest } from "./UpdateAncillaryRequest";
import { ValidateAncillaryDataRequest } from "./ValidateAncillaryDataRequest";
import { ValidateCreateAncillaryRequest } from "./ValidateCreateAncillaryRequest";
import { ValidateMergeAncillariesRequest } from "./ValidateMergeAncillariesRequest";
import { ValidateMergeAncillariesResponse } from "./ValidateMergeAncillariesResponse";
import { ValidateUpdateAncillaryRequest } from "./ValidateUpdateAncillaryRequest";

export const useAssetsManageAncillaryApi = () => {
    const appConfigContext = useContext(AppConfigContext);
    const { httpGet, httpPost, httpPut } = useAuthorizedFetch();
    const baseUrl = `${appConfigContext.catalogueConfig.apiUrl}/ancillaries/manage`;

    const getAncillaryList = async (query: GetAncillaryListRequest): Promise<GetAncillaryListResponse> => {
        return httpGet<GetAncillaryListResponse>(`${baseUrl}/list`, query);
    };

    const validateCreateAncillary = async (request: ValidateCreateAncillaryRequest): Promise<ValidationResult> => {
        return httpGet<ValidationResult>(`${baseUrl}/validate-create`, request);
    };

    const createAncillary = async (createAncillaryRequest: CreateAncillaryRequest): Promise<CreateEntityResponse> => {
        return httpPost<CreateEntityResponse>(`${baseUrl}`, createAncillaryRequest);
    };

    const getAncillariesBySimilarTitle = async (
        request: ValidateAncillaryDataRequest,
        signal: AbortSignal
    ): Promise<GetAncillariesBySimilarTitleResponse> => {
        return httpGet<GetAncillariesBySimilarTitleResponse>(`${baseUrl}/similar`, request, signal);
    };

    const validateUpdateAncillary = async (
        ancillaryId: string,
        request: ValidateUpdateAncillaryRequest
    ): Promise<ValidationResult> => {
        try {
            return await httpGet(`${baseUrl}/${ancillaryId}/validate-update`, request);
        } catch (error) {
            if (error instanceof ValidationError) return validationFailed(error.failures);

            throw error;
        }
    };

    const updateAncillary = async (ancillaryId: string, ancillaryData: UpdateAncillaryRequest): Promise<void> => {
        return httpPut(`${baseUrl}/${ancillaryId}`, ancillaryData);
    };

    const validateMergeAncillaries = async (
        request: ValidateMergeAncillariesRequest
    ): Promise<ValidateMergeAncillariesResponse> => {
        return httpGet<ValidateMergeAncillariesResponse>(`${baseUrl}/validate-merge`, request);
    };

    const mergeAncillaries = async (request: MergeAncillariesRequest): Promise<MergeAncillariesResponse> => {
        return httpPut<MergeAncillariesResponse>(`${baseUrl}/merge`, request);
    };

    return {
        getAncillaryList,
        createAncillary,
        validateCreateAncillary,
        validateUpdateAncillary,
        updateAncillary,
        getAncillariesBySimilarTitle,
        validateMergeAncillaries,
        mergeAncillaries
    };
};
